import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  KeyRound,
  Mail,
  RefreshCw,
  Webhook,
  XCircle,
} from "lucide-react";
import { getHealthMonitor, type HealthSnapshot, type HealthAlert } from "@/lib/health-monitor.functions";
import { requireAdmin } from "@/lib/admin-guard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin_/health-monitor")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Health Monitor — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: HealthMonitorPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-destructive">{error instanceof Error ? error.message : "Page unavailable"}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

function fmtAgo(iso: string | null | undefined) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

type SignalCardProps = {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  triggered: boolean;
  headline: string;
  detail?: React.ReactNode;
};

function SignalCard({ title, icon: Icon, triggered, headline, detail }: SignalCardProps) {
  return (
    <Card className={triggered ? "border-destructive" : undefined}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Icon className="h-4 w-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2">
          {triggered ? (
            <Badge variant="destructive" className="gap-1">
              <XCircle className="h-3 w-3" /> Alert
            </Badge>
          ) : (
            <Badge variant="default" className="gap-1">
              <CheckCircle2 className="h-3 w-3" /> Healthy
            </Badge>
          )}
          <span className="text-sm font-medium">{headline}</span>
        </div>
        {detail && <div className="text-xs text-muted-foreground">{detail}</div>}
      </CardContent>
    </Card>
  );
}

function evaluateSignals(s: HealthSnapshot) {
  const authTop = s.auth_failures?.top_emails?.[0];
  const authTotal = s.auth_failures?.total_15m ?? 0;
  return {
    auth: authTotal >= 30 || (authTop?.count ?? 0) >= 10,
    stuck: (s.membership_stuck?.count ?? 0) > 0,
    dlq: (s.email_dlq?.count_15m ?? 0) > 0,
    webhook: (s.webhook_errors?.count_15m ?? 0) > 0,
  };
}

function HealthMonitorPage() {
  const router = useRouter();
  const fetcher = useServerFn(getHealthMonitor);
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["health-monitor"],
    queryFn: () => fetcher({}),
    refetchInterval: 60_000,
  });

  const snap = data?.snapshot;
  const alerts = data?.alerts ?? [];
  const triggered = snap ? evaluateSignals(snap) : null;
  const openCount = triggered
    ? Number(triggered.auth) + Number(triggered.stuck) + Number(triggered.dlq) + Number(triggered.webhook)
    : 0;

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <button onClick={() => router.navigate({ to: "/admin" })} className="hover:underline">
              Admin
            </button>
            <span>/</span>
            <span>Health Monitor</span>
          </div>
          <h1 className="text-3xl font-semibold flex items-center gap-2 mt-1">
            <Activity className="h-7 w-7 text-primary" />
            Health Monitor
          </h1>
          <p className="text-muted-foreground mt-1">
            Live status of four production signals. A cron job checks every 15 minutes
            and emails admins (with a 60-minute debounce per signal) when any signal trips.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 me-2 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4 text-destructive">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {isLoading && !data && (
        <Card>
          <CardContent className="p-6 text-muted-foreground">Loading…</CardContent>
        </Card>
      )}

      {snap && triggered && (
        <>
          <Card className={openCount > 0 ? "border-destructive" : undefined}>
            <CardContent className="p-4 flex items-center gap-3 text-sm">
              {openCount === 0 ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span>All four signals healthy. Snapshot taken {fmtAgo(snap.generated_at)}.</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  <span>
                    <strong>{openCount}</strong> of 4 signals currently triggered. Snapshot taken {fmtAgo(snap.generated_at)}.
                  </span>
                </>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2">
            <SignalCard
              title="Sign-in failures (last 15m)"
              icon={KeyRound}
              triggered={triggered.auth}
              headline={`${snap.auth_failures.total_15m} failed attempts`}
              detail={
                snap.auth_failures.top_emails.length > 0 ? (
                  <ul className="space-y-0.5">
                    {snap.auth_failures.top_emails.slice(0, 5).map((t) => (
                      <li key={t.email}>
                        {t.email} — <span className="tabular-nums">{t.count}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  "No failed sign-ins recorded."
                )
              }
            />
            <SignalCard
              title="Memberships paid but not activated"
              icon={CreditCard}
              triggered={triggered.stuck}
              headline={`${snap.membership_stuck.count} stuck`}
              detail={
                snap.membership_stuck.rows.length > 0 ? (
                  <ul className="space-y-0.5">
                    {snap.membership_stuck.rows.slice(0, 5).map((r) => (
                      <li key={r.user_id}>
                        {r.email} — status <code>{r.status}</code> (paid {fmtAgo(r.paid_at)})
                      </li>
                    ))}
                  </ul>
                ) : (
                  "Every paid membership is active."
                )
              }
            />
            <SignalCard
              title="Email DLQ (last 15m)"
              icon={Mail}
              triggered={triggered.dlq}
              headline={`${snap.email_dlq.count_15m} email${snap.email_dlq.count_15m === 1 ? "" : "s"} failed`}
              detail={
                snap.email_dlq.rows.length > 0 ? (
                  <ul className="space-y-0.5">
                    {snap.email_dlq.rows.slice(0, 5).map((r) => (
                      <li key={r.message_id}>
                        <code>{r.template_name}</code> → {r.recipient_email}
                        {r.error_message && (
                          <span className="block text-[10px]">{r.error_message.slice(0, 160)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  "No dead-letter emails in the window."
                )
              }
            />
            <SignalCard
              title="Stripe webhook errors (last 15m)"
              icon={Webhook}
              triggered={triggered.webhook}
              headline={`${snap.webhook_errors.count_15m} error${snap.webhook_errors.count_15m === 1 ? "" : "s"}`}
              detail={
                snap.webhook_errors.rows.length > 0 ? (
                  <ul className="space-y-0.5">
                    {snap.webhook_errors.rows.slice(0, 5).map((r, i) => (
                      <li key={i}>
                        <code>{r.event_type}</code>
                        {r.reference && <> — {r.reference}</>}
                        {r.summary && <span className="block text-[10px]">{r.summary}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  "No webhook errors."
                )
              }
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent alert history</CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No alerts have fired yet. As soon as a signal trips, a row will appear here
                  and an email will go out to every address in <code>admin_emails</code>.
                </p>
              ) : (
                <ul className="divide-y">
                  {alerts.map((a: HealthAlert) => (
                    <li key={a.id} className="py-3 flex items-start gap-3">
                      <Badge
                        variant={a.severity === "error" ? "destructive" : "outline"}
                        className="mt-0.5 shrink-0"
                      >
                        {a.severity}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{a.title}</p>
                        {a.body && (
                          <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-0.5">
                            {a.body}
                          </p>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-1">
                          <code>{a.kind}</code> · {new Date(a.created_at).toLocaleString()} ({fmtAgo(a.created_at)})
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
